import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, ExternalLink, Gamepad2, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_streamhub-59/artifacts/n2fgt7ny_maya.png';

const KEYMAP = {
  up: { key: 'ArrowUp', code: 'ArrowUp', keyCode: 38 },
  down: { key: 'ArrowDown', code: 'ArrowDown', keyCode: 40 },
  left: { key: 'ArrowLeft', code: 'ArrowLeft', keyCode: 37 },
  right: { key: 'ArrowRight', code: 'ArrowRight', keyCode: 39 },
  a: { key: ' ', code: 'Space', keyCode: 32 },
  b: { key: 'x', code: 'KeyX', keyCode: 88 },
  start: { key: 'Enter', code: 'Enter', keyCode: 13 },
};

export default function GamePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);
  const [barVisible, setBarVisible] = useState(true);
  const [padOverlay, setPadOverlay] = useState(false);
  const [room, setRoom] = useState(null);
  const [players, setPlayers] = useState(0);
  const hideTimer = useRef(null);
  const iframeRef = useRef(null);
  const wsRef = useRef(null);
  const tvMode = sessionStorage.getItem('tvMode') === '1';

  useEffect(() => {
    axios.get(`${API}/contents/${id}`, { withCredentials: true })
      .then(r => setContent(r.data))
      .catch(() => setError(true));
  }, [id]);

  useEffect(() => {
    hideTimer.current = setTimeout(() => setBarVisible(false), 5000);
    const show = () => {
      setBarVisible(true);
      clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setBarVisible(false), 4000);
    };
    window.addEventListener('mousemove', show);
    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Backspace' || e.keyCode === 10009 || e.keyCode === 461) {
        e.preventDefault();
        if (sessionStorage.getItem('tvMode')) navigate('/tv', { replace: true });
        else navigate(-1);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(hideTimer.current);
      window.removeEventListener('mousemove', show);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [navigate]);

  // ── Phone gamepad: forward inputs to the game (Option B: same-origin key injection, Option A: postMessage for the SDK) ──
  const forwardInput = useCallback((msg) => {
    const ifr = iframeRef.current;
    if (!ifr || (msg.type !== 'press' && msg.type !== 'release')) return;
    const m = KEYMAP[msg.key];
    if (!m) return;
    try { ifr.contentWindow.postMessage({ ...msg, source: 'mayaplus-gamepad' }, '*'); } catch { /* ignore */ }
    try {
      const win = ifr.contentWindow;
      const doc = win.document; // throws if cross-origin
      const ev = new KeyboardEvent(msg.type === 'press' ? 'keydown' : 'keyup', {
        key: m.key, code: m.code, keyCode: m.keyCode, which: m.keyCode, bubbles: true, cancelable: true,
      });
      try { Object.defineProperty(ev, 'keyCode', { get: () => m.keyCode }); } catch { /* ignore */ }
      try { Object.defineProperty(ev, 'which', { get: () => m.keyCode }); } catch { /* ignore */ }
      win.dispatchEvent(ev);
      doc.dispatchEvent(ev);
      const canvas = doc.querySelector('canvas');
      if (canvas) canvas.dispatchEvent(ev);
    } catch { /* cross-origin: SDK postMessage only */ }
  }, []);

  const openPadOverlay = async () => {
    setPadOverlay(true);
    if (room) return;
    try {
      const { data } = await axios.post(`${API}/gamepad/room`, {}, { withCredentials: true });
      setRoom(data.room);
      const ws = new WebSocket(`${WS_BASE}/api/gamepad/ws/tv/${data.room}`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (d.type === 'pad_connected' || d.type === 'pad_disconnected' || d.type === 'ready') setPlayers(d.players || 0);
          else forwardInput(d);
        } catch { /* ignore */ }
      };
    } catch { /* ignore */ }
  };

  useEffect(() => () => { wsRef.current?.close(); }, []);

  const gameUrl = content?.game_url || content?.video_url;
  const padUrl = room ? `${window.location.origin}/manette/${room}` : '';

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">Jeu introuvable</p>
          <button onClick={() => navigate(-1)} className="px-6 py-2.5 bg-[#E9641A] text-white rounded-lg font-semibold" data-testid="game-error-back">Retour</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black" data-testid="game-player-page">
      {gameUrl && (
        <iframe
          ref={iframeRef}
          src={gameUrl}
          title={content?.title || 'Jeu'}
          className="w-full h-full border-0"
          allow="fullscreen; gamepad; autoplay; pointer-lock"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-forms allow-pointer-lock allow-popups-to-escape-sandbox"
          data-testid="game-iframe"
        />
      )}
      {/* Top bar (auto-hide) */}
      <div
        className={`absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-3 bg-gradient-to-b from-black/90 to-transparent transition-opacity duration-500 ${barVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        data-testid="game-topbar"
      >
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Maya+" className="h-7" />
          <div className="flex items-center gap-2 text-white/80">
            <Gamepad2 size={16} className="text-[#E9641A]" />
            <span className="text-sm font-semibold">{content?.title || ''}</span>
          </div>
          {players > 0 && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500/15 text-green-400 text-[10px] font-semibold rounded-full" data-testid="pad-connected-badge">
              <Smartphone size={11} /> {players} manette{players > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openPadOverlay}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white/80 text-xs rounded-lg hover:bg-white/20 transition-colors"
            data-testid="game-gamepad-btn">
            <Smartphone size={13} /> Manette mobile
          </button>
          {!tvMode && gameUrl && (
            <a href={gameUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white/70 text-xs rounded-lg hover:bg-white/20 transition-colors"
              title="Si le jeu ne se charge pas dans Maya+" data-testid="game-open-external">
              <ExternalLink size={13} /> Nouvel onglet
            </a>
          )}
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[#E9641A] text-white text-xs font-semibold rounded-lg hover:bg-[#FE975E] transition-colors"
            data-testid="game-exit-btn">
            <X size={14} /> Quitter{tvMode ? ' (Retour)' : ''}
          </button>
        </div>
      </div>

      {/* QR overlay: pair the phone controller */}
      {padOverlay && (
        <div className="absolute inset-0 z-30 bg-black/85 backdrop-blur-sm flex items-center justify-center" data-testid="gamepad-qr-overlay">
          <div className="bg-[#1a0e2e] border border-white/10 rounded-2xl p-8 max-w-md text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Smartphone size={18} className="text-[#E9641A]" />
              <h3 className="text-lg font-bold text-white">Votre téléphone devient une manette</h3>
            </div>
            <p className="text-white/45 text-xs mb-5">Scannez ce QR code avec l'appareil photo de votre téléphone</p>
            {padUrl ? (
              <div className="bg-white rounded-xl p-4 inline-block mb-4" data-testid="gamepad-qr-code">
                <QRCodeSVG value={padUrl} size={200} level="M" />
              </div>
            ) : (
              <p className="text-white/40 text-sm mb-4">Génération du code…</p>
            )}
            {room && <p className="text-white/35 text-[11px] mb-1">ou ouvrez <span className="text-white/70 font-semibold">{window.location.host}/manette/{room}</span></p>}
            <p className={`text-xs font-semibold mb-5 ${players > 0 ? 'text-green-400' : 'text-white/30'}`} data-testid="gamepad-players-status">
              {players > 0 ? `✓ ${players} manette${players > 1 ? 's' : ''} connectée${players > 1 ? 's' : ''}` : 'En attente de connexion…'}
            </p>
            <button onClick={() => setPadOverlay(false)}
              className="px-8 py-2.5 bg-[#E9641A] text-white rounded-lg text-sm font-semibold hover:bg-[#FE975E] transition-colors"
              data-testid="gamepad-overlay-close">
              {players > 0 ? 'Jouer' : 'Fermer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
